import React, { useState, useMemo } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  styled,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import moment from 'moment'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import { toast } from 'react-toastify'
import { SaleIntegrity } from '../../types/SaleIntegrity'
import {
  useDeleteDealerSaleMutation,
  useReprocessSaleDataMutation,
  useDealerSaleBulkDeleteMutation,
} from '../../redux/api/SaleIntegrityApi'
import { BulkUpdateSaleRecord } from '../../pages/SaleIntegrity/BulkUpdateSaleRecord'

const CompactTableCell = styled(TableCell)({
  padding: '6px 10px',
  fontSize: '0.825rem',
  whiteSpace: 'nowrap',
})

const CompactTableRow = styled(TableRow)({
  height: '60px',
  '&:nth-of-type(odd)': {
    backgroundColor: '#fcfcfc',
  },
})

const btnStyle = {
  textTransform: 'none',
  width: '100px',
  borderRadius: '20px',
  textDecoration: 'none',
}

const actionBtnStyle = {
  textTransform: 'none',
  textDecoration: 'none',
  fontSize: '0.7rem',
  padding: '2px 8px',
  minWidth: 'auto',
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) return -1
  if (b[orderBy] > a[orderBy]) return 1
  return 0
}

type Order = 'asc' | 'desc'

function getComparator<Key extends keyof SaleIntegrity>(order: Order, orderBy: Key) {
  return order === 'desc'
    ? (a: SaleIntegrity, b: SaleIntegrity) => descendingComparator(a, b, orderBy)
    : (a: SaleIntegrity, b: SaleIntegrity) => -descendingComparator(a, b, orderBy)
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

interface HeadCell {
  id: keyof SaleIntegrity
  label: string
}

const headCells: readonly HeadCell[] = [
  { id: 'dealerSalesId', label: 'Dealer Sales ID' },
  { id: 'dealerGroupNumber', label: 'Dealer Group Number' },
  { id: 'dealerId', label: 'Dealer ID' },
  { id: 'invoiceTicketNumber', label: 'Invoice Ticket Number' },
  { id: 'subcategory', label: 'Subcategory' },
  { id: 'invoiceDt', label: 'Invoice Date' },
  { id: 'isOnlinePurchaseFlg', label: 'Online Purchase' },
  { id: 'protectionPlanType', label: 'Protection Plan Type' },
  { id: 'retailProtectionPrice', label: 'Retail Protection Price' },
  { id: 'totalRetailOfCoveredItems', label: 'Total Retail Covered Items' },
  { id: 'totalInvoiceAmount', label: 'Total Invoice Amount' },
  { id: 'tax', label: 'Tax' },
  { id: 'deliveryDt', label: 'Delivery Date' },
  { id: 'isRentToOwnPurchaseFlg', label: 'Rent To Own Purchase' },
  { id: 'itemDescription', label: 'Item Description' },
  { id: 'invoiceLineNumber', label: 'Invoice Line Number' },
  { id: 'sku', label: 'SKU' },
  { id: 'brand', label: 'Brand' },
  { id: 'model', label: 'Model' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'retailPrice', label: 'Retail Price' },
  { id: 'serial', label: 'Serial' },
  { id: 'materialType', label: 'Material Type' },
  { id: 'firstName', label: 'First Name' },
  { id: 'lastName', label: 'Last Name' },
  { id: 'address1', label: 'Address 1' },
  { id: 'address2', label: 'Address 2' },
  { id: 'city', label: 'City' },
  { id: 'state', label: 'State' },
  { id: 'postalCode', label: 'Postal Code' },
  { id: 'country', label: 'Country' },
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Phone' },
  { id: 'hasPhoneNoSolicitFlg', label: 'Phone No Solicit' },
  { id: 'hasEmailNoSolicitFlg', label: 'Email No Solicit' },
  { id: 'squareFootage', label: 'Square Footage' },
  { id: 'realtorProgram', label: 'Realtor Program' },
  { id: 'oemWarrantyCertificate', label: 'OEM Warranty Certificate' },
  { id: 'homeClosingDt', label: 'Home Closing Date' },
  { id: 'installDt', label: 'Install Date' },
  { id: 'planPurchaseDt', label: 'Plan Purchase Date' },
  { id: 'uploadProofOfPurchase', label: 'Upload Proof Of Purchase' },
  { id: 'salesPersonId', label: 'Sales Person ID' },
  { id: 'contractNumber', label: 'Contract Number' },
  { id: 'contractSourceName', label: 'Contract Source Name' },
  { id: 'contractCreateError', label: 'Contract Create Error' },
  { id: 'contractCreateErrorDetail', label: 'Contract Create Error Detail' },
  { id: 'application', label: 'Application' },
  { id: 'createdBy', label: 'Created By' },
  { id: 'createdOn', label: 'Created On' },
  { id: 'updatedBy', label: 'Updated By' },
  { id: 'updatedOn', label: 'Updated On' },
  { id: 'realtorFirstName', label: 'Realtor First Name' },
  { id: 'realtorLastName', label: 'Realtor Last Name' },
  { id: 'realtorEmail', label: 'Realtor Email' },
  { id: 'loanNumber', label: 'Loan Number' },
]

interface DealerSaleTableHeadProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof SaleIntegrity) => void
  order: Order
  orderBy: string
  allVisibleSelected: boolean
  someVisibleSelected: boolean
  onSelectAll: (checked: boolean) => void
}

function DealerSaleTableHead(props: DealerSaleTableHeadProps) {
  const { onRequestSort, order, orderBy, allVisibleSelected, someVisibleSelected, onSelectAll } =
    props
  const createSortHandler =
    (property: keyof SaleIntegrity) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }

  return (
    <TableHead>
      <TableRow sx={{ backgroundColor: '#1976d2' }}>
        <CompactTableCell align='center' sx={{ color: 'white', fontWeight: 'bold', width: '50px' }}>
          <Checkbox
            size='small'
            checked={allVisibleSelected}
            indeterminate={someVisibleSelected && !allVisibleSelected}
            onChange={(e) => onSelectAll(e.target.checked)}
            sx={{
              color: 'white',
              '&.Mui-checked': { color: 'white' },
              '&.MuiCheckbox-indeterminate': { color: 'white' },
            }}
          />
        </CompactTableCell>
        <CompactTableCell
          align='center'
          sx={{ color: 'white', minWidth: '150px', fontWeight: 'bold' }}
        >
          Actions
        </CompactTableCell>
        {headCells.map((headCell) => (
          <CompactTableCell
            key={headCell.id}
            align='center'
            sx={{ fontWeight: 'bold' }}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              sx={{
                color: 'white',
                '& .MuiTableSortLabel-icon': { color: 'white' },
              }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component='span' sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </CompactTableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

interface SaleIntegrityTableProps {
  isLoading: boolean
  rows: SaleIntegrity[]
  searchKey: string
  editStatus: (status: SaleIntegrity) => void
  refetch: () => void
}

export const SaleIntegrityPageTable = ({
  isLoading,
  rows,
  searchKey,
  editStatus,
  refetch,
}: SaleIntegrityTableProps) => {
  const [deleteDealerSale] = useDeleteDealerSaleMutation()
  const [reprocessSale] = useReprocessSaleDataMutation()
  const [dealerSaleBulkDelete, { isLoading: isBulkDeleting }] = useDealerSaleBulkDeleteMutation()

  // ─── Single delete state ───────────────────────────────────────────────────
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)
  const [saleToDelete, setSaleToDelete] = useState<SaleIntegrity | null>(null)

  // ─── Bulk delete state ─────────────────────────────────────────────────────
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState<boolean>(false)

  // ─── Bulk update state ─────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [rowData, setRowData] = useState()
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false)

  // ─── Table state ───────────────────────────────────────────────────────────
  const [order, setOrder] = useState<Order>('desc')
  const [orderBy, setOrderBy] = useState<keyof SaleIntegrity>('createdOn')
  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(20)
  const [processingId, setProcessingId] = useState<number | null>(null)

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof SaleIntegrity) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleDeleteClick = (row: SaleIntegrity) => {
    setSaleToDelete(row)
    setDeleteDialogOpen(true)
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setSaleToDelete(null)
  }

  const handleConfirmDelete = async () => {
    if (saleToDelete !== null) {
      try {
        await deleteDealerSale({
          dealerSalesId: saleToDelete.dealerSalesId,
          rowData: saleToDelete,
        }).unwrap()
        toast.success('Record deleted successfully')
        refetch()
      } catch (error) {
        toast.error('Failed to delete record')
      }
    }
    setDeleteDialogOpen(false)
    setSaleToDelete(null)
  }

  const handleBulkDeleteConfirm = async () => {
    try {
      const result = await dealerSaleBulkDelete({
        dealerSalesIds: Array.from(selectedIds),
      }).unwrap()

      if (result.status === 'success') {
        toast.success(result.message || `${selectedIds.size} record(s) deleted successfully`)
      } else if (result.status === 'partial') {
        toast.warning(result.message || 'Some records failed to delete')
      } else {
        toast.error(result.message || 'Failed to delete records')
      }

      setSelectedIds(new Set())
      refetch()
    } catch (error: any) {
      toast.error(error?.data?.message || 'Bulk delete failed')
    } finally {
      setBulkDeleteDialogOpen(false)
    }
  }

  const handleProcessClick = async (row: SaleIntegrity) => {
    setProcessingId(row.dealerSalesId)
    try {
      const response = await reprocessSale({
        dealerSalesData: row,
      }).unwrap()
      toast.success(response?.message || 'Contract Created Successfully!')
      refetch()
    } catch (error: any) {
      toast.error(error?.data?.message || 'Processing Failed')
      refetch()
    } finally {
      setProcessingId(null)
    }
  }

  const filteredRowsWithSearch = useMemo(() => {
    if (!rows || rows.length === 0) return []
    if (!searchKey) return rows

    const lowerSearch = searchKey.toLowerCase()

    return rows.filter((row) => {
      return (
        String(row.dealerGroupNumber || '')
          .toLowerCase()
          .includes(lowerSearch) ||
        String(row.dealerId || '')
          .toLowerCase()
          .includes(lowerSearch) ||
        String(row.invoiceTicketNumber || '')
          .toLowerCase()
          .includes(lowerSearch) ||
        String(row.firstName || '')
          .toLowerCase()
          .includes(lowerSearch) ||
        String(row.lastName || '')
          .toLowerCase()
          .includes(lowerSearch)
      )
    })
  }, [rows, searchKey])

  const sortedRowsWithPagination = useMemo(
    () =>
      stableSort(filteredRowsWithSearch, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [filteredRowsWithSearch, order, orderBy, page, rowsPerPage],
  )

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const visibleIds = sortedRowsWithPagination.map((row) => row.dealerSalesId)
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id))
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id))

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds((prev) => {
      const updated = new Set(prev)
      if (checked) {
        visibleIds.forEach((id) => updated.add(id))
      } else {
        visibleIds.forEach((id) => updated.delete(id))
      }
      return updated
    })
  }

  const handleRowCheckbox = (row: SaleIntegrity, id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const updated = new Set(prev)
      if (checked) {
        updated.add(id)
      } else {
        updated.delete(id)
      }
      return updated
    })
  }

  const handleBulkUpdateClose = () => {
    setBulkUpdateOpen(false)
  }

  const handleBulkUpdateSuccess = () => {
    setSelectedIds(new Set())
    refetch()
  }

  return (
    <React.Fragment>
      {/* ─── Top action buttons ────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          variant='contained'
          color='primary'
          disabled={selectedIds.size === 0}
          onClick={() => setBulkUpdateOpen(true)}
          size='large'
          sx={{ textDecoration: 'none' }}
        >
          Update {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
        </Button>
        <Button
          variant='contained'
          color='error'
          disabled={selectedIds.size === 0}
          onClick={() => setBulkDeleteDialogOpen(true)}
          size='large'
          sx={{ textDecoration: 'none', marginLeft: '10px' }}
        >
          Delete {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
        </Button>
      </Box>

      {/* ─── Table ───────────*/}
      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
        <Table size='small'>
          <DealerSaleTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            allVisibleSelected={allVisibleSelected}
            someVisibleSelected={someVisibleSelected}
            onSelectAll={handleSelectAll}
          />
          <TableBody>
            {isLoading ? (
              <TableRow>
                <CompactTableCell colSpan={headCells.length + 2} align='center'>
                  <CircularProgress size={24} sx={{ my: 2 }} />
                </CompactTableCell>
              </TableRow>
            ) : filteredRowsWithSearch.length > 0 ? (
              sortedRowsWithPagination.map((row) => {
                const isThisRowProcessing = processingId === row.dealerSalesId
                const isChecked = selectedIds.has(row.dealerSalesId)

                return (
                  <CompactTableRow hover tabIndex={-1} key={row.dealerSalesId}>
                    <CompactTableCell align='center'>
                      <Checkbox
                        size='small'
                        checked={isChecked}
                        onChange={(e) =>
                          handleRowCheckbox(row, row.dealerSalesId, e.target.checked)
                        }
                      />
                    </CompactTableCell>

                    <CompactTableCell align='center'>
                      <Stack spacing={0.5} direction='row' justifyContent='center'>
                        <IconButton
                          size='small'
                          onClick={() => editStatus(row)}
                          disabled={isThisRowProcessing}
                        >
                          <EditIcon fontSize='small' />
                        </IconButton>
                        <IconButton
                          size='small'
                          onClick={() => handleDeleteClick(row)}
                          disabled={isThisRowProcessing}
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                        <Button
                          variant='contained'
                          color='primary'
                          size='small'
                          onClick={() => handleProcessClick(row)}
                          disabled={isThisRowProcessing}
                          sx={actionBtnStyle}
                        >
                          {isThisRowProcessing ? (
                            <CircularProgress size={14} sx={{ color: 'white' }} />
                          ) : (
                            'Process'
                          )}
                        </Button>
                      </Stack>
                    </CompactTableCell>

                    {headCells.map((headCell) => {
                      const value = row[headCell.id]
                      let content = value !== null && value !== undefined ? String(value) : ''

                      if (
                        String(headCell.id).endsWith('Dt') ||
                        String(headCell.id).endsWith('On')
                      ) {
                        content = value ? moment(value as string).format('MM/DD/YYYY') : ''
                      }

                      if (String(headCell.id).endsWith('Flg')) {
                        content = value === 1 || value === '1' || value === 'Yes' ? 'Yes' : 'No'
                      }

                      return (
                        <CompactTableCell key={headCell.id} align='center'>
                          {content}
                        </CompactTableCell>
                      )
                    })}
                  </CompactTableRow>
                )
              })
            ) : (
              <TableRow>
                <CompactTableCell
                  colSpan={headCells.length + 2}
                  sx={{ padding: '20px', fontWeight: 'bold' }}
                  align='center'
                >
                  No records found
                </CompactTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[20, 40, 60]}
        component='div'
        count={filteredRowsWithSearch.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ borderTop: '1px solid #e0e0e0' }}
      />

      {/* ─── Single delete confirmation dialog ──── */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth='xs'
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center' }}>
            Are you sure you want to delete this record?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', paddingBottom: '16px' }}>
          <Button onClick={handleCancelDelete} variant='outlined' sx={btnStyle}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color='error'
            variant='contained'
            sx={btnStyle}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Bulk delete confirmation dialog ──────────*/}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => !isBulkDeleting && setBulkDeleteDialogOpen(false)}
        maxWidth='xs'
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          Confirm Bulk Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center' }}>
            Are you sure you want to delete <strong>{selectedIds.size} selected record(s)</strong>?{' '}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', paddingBottom: '16px' }}>
          <Button
            onClick={() => setBulkDeleteDialogOpen(false)}
            variant='outlined'
            disabled={isBulkDeleting}
            sx={btnStyle}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDeleteConfirm}
            color='error'
            variant='contained'
            disabled={isBulkDeleting}
            sx={btnStyle}
            autoFocus
          >
            {isBulkDeleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Bulk update modal ──── */}
      <BulkUpdateSaleRecord
        open={bulkUpdateOpen}
        onClose={handleBulkUpdateClose}
        selectedCount={selectedIds.size}
        selectedIds={Array.from(selectedIds)}
        onSuccess={handleBulkUpdateSuccess}
      />
    </React.Fragment>
  )
}
